import React, { useEffect, useCallback } from 'react';
import { Card, ListGroup, Dropdown, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { FiBell, FiClock, FiUser } from 'react-icons/fi';
import avatar2 from '../../../../assets/images/user/avatar-2.jpg';
import { getSessionStorage, isSessionValueReady } from '../../../../utils/helpers';
import {
    deleteUserNotification,
    getUserNotifications,
    reviewSkillChangeNotification,
} from '../../../../redux/features/Notification/notificationSlice';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import styled from 'styled-components';

const SKILL_CHANGE_REQUEST_TYPE = 'SkillChangeRequest';

const StyledDeleteButton = styled.button`
    background-color: #dc3545;
    border: none;
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 5px 10px;
    font-size: 18px;
    line-height: 1;
    cursor: pointer;
    border-radius: 4px;
    flex-shrink: 0;

    &:hover {
        background-color: #c82333;
    }

    &:focus {
        outline: none;
        box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.5);
    }
`;

const SkillReviewActions = styled.div`
    display: flex;
    gap: 8px;
    margin-top: 10px;
`;

const parseSkillChangeRequestId = (item) => {
    if (item?.skillChangeRequestId != null) {
        return item.skillChangeRequestId;
    }
    const url = item?.url || item?.Url || '';
    const prefix = 'skill-change-request/';
    if (url.includes(prefix)) {
        const idPart = url.split(prefix)[1]?.split('/')[0]?.split('?')[0];
        if (idPart && /^\d+$/.test(idPart)) {
            return parseInt(idPart, 10);
        }
    }
    const reviewMatch = url.match(/SkillChangeReview[/\\?&](?:requestId=)?(\d+)/i);
    if (reviewMatch) {
        return parseInt(reviewMatch[1], 10);
    }
    return null;
};

const isSkillChangeRequestNotification = (item) => {
    const type = item?.notificationType || item?.NotificationType || '';
    return type === SKILL_CHANGE_REQUEST_TYPE;
};

const NavRight = () => {
    const permissionLevel = getSessionStorage('PERMISSION_LEVEL');
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const notifications = useSelector((state) => state.userNotifications.notificationList);
    const notificationBusy = useSelector((state) => state.userNotifications.isLoading);

    const refreshNotifications = useCallback(() => {
        const uid = getSessionStorage('USER_ID');
        if (isSessionValueReady(uid)) {
            dispatch(getUserNotifications(uid));
        }
    }, [dispatch]);

    useEffect(() => {
        refreshNotifications();
    }, [refreshNotifications]);

    const handleDeleteClick = (id) => {
        dispatch(
            deleteUserNotification({
                data: {
                    id,
                    loggedUserID: getSessionStorage('USER_ID'),
                },
            })
        );
    };

    const handleSkillReview = (item, action) => {
        const requestId = parseSkillChangeRequestId(item);
        if (!requestId) {
            return;
        }
        dispatch(
            reviewSkillChangeNotification({
                loggedUserID: getSessionStorage('USER_ID'),
                requestId,
                action,
            })
        );
    };

    return (
        <React.Fragment>
            <ListGroup as="ul" bsPrefix=" " className="navbar-nav ml-auto" id="navbar-right" style={{ color: '#ffffff' }}>
                <ListGroup.Item as="li" bsPrefix=" ">
                    <Dropdown align="start">
                        <Dropdown.Toggle
                            as={Link}
                            variant="link"
                            to="#"
                            id="dropdown-basic"
                            className="d-inline-flex align-items-center text-white text-decoration-none"
                        >
                            <FiBell size={22} aria-hidden className="flex-shrink-0" />
                            <span className="visually-hidden">Notifications</span>
                        </Dropdown.Toggle>
                        <Dropdown.Menu align="end" className="notification notification-scroll">
                            <div className="noti-head">
                                <h6 className="d-inline-block m-b-0">Notifications</h6>
                            </div>
                            <PerfectScrollbar>
                                <ListGroup as="ul" bsPrefix=" " variant="flush" className="noti-body">
                                    {notifications && notifications.length === 0 && (
                                        <ListGroup.Item as="li" bsPrefix=" " className="notification text-muted px-3 py-2">
                                            No notifications
                                        </ListGroup.Item>
                                    )}
                                    {notifications &&
                                        notifications.map((item, i) => {
                                            const isSkillRequest = isSkillChangeRequestNotification(item);
                                            return (
                                                <ListGroup.Item as="li" bsPrefix=" " className="notification" key={item?.id ?? i}>
                                                    <Card
                                                        className="d-flex align-items-start shadow-none mb-0 p-0"
                                                        style={{ flexDirection: 'row', backgroundColor: 'unset' }}
                                                    >
                                                        <img className="img-radius" src={avatar2} alt="" />
                                                        <Card.Body className="p-0 ps-2 flex-grow-1">
                                                            <p className="mb-1">
                                                                <strong>{item?.title}</strong>
                                                                <span className="n-time text-muted">
                                                                    <FiClock className="me-2 flex-shrink-0" size={14} aria-hidden />
                                                                    {moment(item?.notificationDate).format('h:mm:ss A')}
                                                                </span>
                                                            </p>
                                                            <p className="mb-0">{item?.description}</p>
                                                            {isSkillRequest && (
                                                                <SkillReviewActions>
                                                                    <Button
                                                                        variant="success"
                                                                        size="sm"
                                                                        disabled={notificationBusy}
                                                                        onClick={() => handleSkillReview(item, 'Approve')}
                                                                    >
                                                                        Accept
                                                                    </Button>
                                                                    <Button
                                                                        variant="outline-danger"
                                                                        size="sm"
                                                                        disabled={notificationBusy}
                                                                        onClick={() => handleSkillReview(item, 'Reject')}
                                                                    >
                                                                        Reject
                                                                    </Button>
                                                                </SkillReviewActions>
                                                            )}
                                                        </Card.Body>
                                                        <StyledDeleteButton
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleDeleteClick(item?.id ?? item?.Id);
                                                            }}
                                                            aria-label="delete"
                                                        >
                                                            &#10006;
                                                        </StyledDeleteButton>
                                                    </Card>
                                                </ListGroup.Item>
                                            );
                                        })}
                                </ListGroup>
                            </PerfectScrollbar>
                        </Dropdown.Menu>
                    </Dropdown>
                </ListGroup.Item>
                <ListGroup.Item
                    as="li"
                    bsPrefix=" "
                    className="cursor-pointer me-2 d-inline-flex align-items-center"
                    onClick={() =>
                        navigate(
                            permissionLevel === 'External'
                                ? '/Dashboard/ExternalUserProfile'
                                : '/Dashboard/UserProfile'
                        )
                    }
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            navigate(
                                permissionLevel === 'External'
                                    ? '/Dashboard/ExternalUserProfile'
                                    : '/Dashboard/UserProfile'
                            );
                        }
                    }}
                    aria-label="Open profile"
                >
                    <FiUser size={22} aria-hidden />
                </ListGroup.Item>
            </ListGroup>
        </React.Fragment>
    );
};

export default NavRight;
